// Fire-and-forget event tracker. Batches events and flushes every 5s,
// on pagehide via sendBeacon. Respects cookie consent.

import { supabase } from '@/integrations/supabase/client';
import { getSessionId, getSessionMeta, isNewSession, parseUtm } from './session';

type EventCategory =
  | 'pageview'
  | 'listing'
  | 'search'
  | 'wizard'
  | 'auth'
  | 'contact'
  | 'share'
  | 'outbound'
  | 'checkout'
  | 'engagement';

export interface TrackEventInput {
  event_name: string;
  event_category?: EventCategory;
  path?: string;
  asset_type?: string | null;
  asset_id?: string | null;
  properties?: Record<string, unknown>;
}

interface QueuedEvent extends TrackEventInput {
  session_id: string;
  user_id: string | null;
  referrer: string | null;
  created_at: string;
}

const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH = 25;
let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let sessionTouched = false;
let currentUserId: string | null = null;

function consentOk(): boolean {
  try {
    const c = localStorage.getItem('cookie-consent');
    // Treat absence as opt-in for first-party anonymous analytics;
    // explicit 'necessary' or 'all' both allow it.
    return c !== null ? c === 'all' || c === 'necessary' : true;
  } catch {
    return true;
  }
}

async function ensureSessionRow() {
  if (sessionTouched) return;
  sessionTouched = true;
  const session_id = getSessionId();
  const meta = getSessionMeta();
  const utm = parseUtm(window.location.search);
  try {
    if (isNewSession()) {
      await supabase.from('analytics_sessions').upsert(
        {
          session_id,
          user_id: currentUserId,
          entry_path: window.location.pathname,
          referrer: document.referrer || null,
          device_type: meta.deviceType,
          browser: meta.browser,
          os: meta.os,
          ...utm,
        },
        { onConflict: 'session_id' }
      );
    } else {
      await supabase
        .from('analytics_sessions')
        .update({
          last_seen_at: new Date().toISOString(),
          exit_path: window.location.pathname,
          user_id: currentUserId ?? undefined,
        })
        .eq('session_id', session_id);
    }
  } catch {
    /* ignore */
  }
}

export function setAnalyticsUser(userId: string | null) {
  currentUserId = userId;
}

export function trackEvent(input: TrackEventInput) {
  if (!consentOk()) return;
  try {
    const session_id = getSessionId();
    queue.push({
      ...input,
      session_id,
      user_id: currentUserId,
      referrer: document.referrer || null,
      created_at: new Date().toISOString(),
      path: input.path ?? window.location.pathname,
    });
    void ensureSessionRow();
    if (queue.length >= MAX_BATCH) void flush();
  } catch {
    /* ignore */
  }
}

export function trackPageview(path?: string) {
  trackEvent({
    event_name: 'page_view',
    event_category: 'pageview',
    path: path ?? window.location.pathname,
  });
}

async function flush() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  try {
    await supabase.from('analytics_events').insert(
      batch.map((e) => ({
        session_id: e.session_id,
        user_id: e.user_id,
        event_name: e.event_name,
        event_category: e.event_category ?? null,
        path: e.path ?? null,
        referrer: e.referrer,
        properties: (e.properties ?? {}) as never,
        asset_type: e.asset_type ?? null,
        asset_id: e.asset_id ?? null,
      }))
    );
  } catch {
    /* drop on error */
  }
}

function beaconFlush() {
  if (queue.length === 0) return;
  // sendBeacon to Supabase REST requires headers we can't set. Fall back to fetch keepalive.
  const batch = queue.splice(0, queue.length);
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/analytics_events`;
    fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        Prefer: 'return=minimal',
      },
      body: JSON.stringify(
        batch.map((e) => ({
          session_id: e.session_id,
          user_id: e.user_id,
          event_name: e.event_name,
          event_category: e.event_category ?? null,
          path: e.path ?? null,
          referrer: e.referrer,
          properties: e.properties ?? {},
          asset_type: e.asset_type ?? null,
          asset_id: e.asset_id ?? null,
        }))
      ),
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

export function startAnalytics() {
  if (flushTimer) return;
  flushTimer = setInterval(() => void flush(), FLUSH_INTERVAL_MS);
  window.addEventListener('pagehide', beaconFlush);
  window.addEventListener('beforeunload', beaconFlush);
}
